/**
 * Extruder dashboard for a lamination line — motor current, screw speed and run
 * events per side, over a live status panel.
 *
 * Both lines run the same PLC program and are served by the same endpoints
 * (see `api/lamLine.ts`), so one component serves both and is handed the line's
 * calls. Lamination 1 used to have its own copy of this reading the blender
 * PLC's extruder columns out of kgreading; those went dead when Line 1's PLC
 * was replaced, and its signals moved onto the line's own PLC.
 *
 * The endpoints return A and B on the same row, so one fetch fills both sides.
 *
 * Run/idle bands come from the plant-wide settings_extruders row, the same
 * values the overview diagrams color by — see utils/screwSpeed.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import MotorCurrentChart from "../pages/Lamination1/charts/MotorCurrentChart";
import ScrewSpeedChart from "../pages/Lamination1/charts/ScrewSpeedChart";
import { LoadingOverlay } from "./LoadingOverlay";
import ExtruderEventTable2, {
  type ExtruderStatusPoint,
} from "../pages/Lamination1/charts/ExtruderEventTable2";
import { useExtruderThresholds } from "../../utils/useExtruderThresholds";
import { useStickyQuickRange } from "../../utils/uiPrefs";
import type { LamApi, LamCurrentStatus } from "../../api/lamLine";
import {
  asNashvilleLocal,
  nowAsNashvilleLocal,
  nashvilleLocalToISO,
} from "../../utils/nashvilleTime";
import { ChartZoomToolbar } from "./chartZoom";
import { format } from "date-fns";

type Point = { timestamp: number; value: number | null };
type Side = "a" | "b";

type SideOption = { id: Side; label: string };

const SIDE_OPTIONS: SideOption[] = [
  { id: "a", label: "Extruder A" },
  { id: "b", label: "Extruder B" },
];

/** Quick-range presets, in hours. 12H is the default window. */
const QUICK_RANGES = [1, 4, 12, 24, 72, 168, 720] as const;
const DEFAULT_RANGE_HRS = 12;

const CURRENT_STATUS_REFRESH_MS = 30_000;
// Auto-refresh chart data on this interval (matches the foam dashboard)
const CHART_REFRESH_MS = 60_000;
// Adaptive downsampling target; server buckets the window to ~this many points.
const CHART_MAX_POINTS = 1500;

function toValidDate(input: number | string | Date): Date | null {
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  if (typeof input === "number") {
    const ms = input > 1e12 ? input : input * 1000;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof input === "string") {
    let s = input.trim();
    if (!s.includes("T") && s.includes(" ")) s = s.replace(" ", "T");
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}
const toMs = (t: string | number | undefined) => {
  if (t == null) return NaN;
  const d = toValidDate(t);
  return d ? d.getTime() : NaN;
};
const finite = (d: Point) =>
  Number.isFinite(d.timestamp) &&
  typeof d.value === "number" &&
  Number.isFinite(d.value);

const emptyBySide = <T,>(): Record<Side, T[]> => ({ a: [], b: [] });

type Props = {
  /** The line's calls, from `makeLamApi("/lam1" | "/lam2")`. */
  api: LamApi;
  /** Namespaces the remembered quick range, e.g. "lam1". */
  prefsKey: string;
};

const LamExtruderDashboard: React.FC<Props> = ({ api, prefsKey }) => {
  // settings_extruders is one plant-wide row, so both lines read it here
  // rather than each wrapper deciding whether to.
  const thresholds = useExtruderThresholds();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Controls. The window opens on whichever preset was last chosen here,
  // recomputed from now — a remembered range, not a remembered instant.
  const [preferredHrs, setPreferredHrs] = useStickyQuickRange(
    `extruders.${prefsKey}`,
    QUICK_RANGES,
    DEFAULT_RANGE_HRS
  );
  const [start, setStart] = useState<Date | null>(() =>
    asNashvilleLocal(new Date(Date.now() - preferredHrs * 3600_000))
  );
  const [end, setEnd] = useState<Date | null>(() => nowAsNashvilleLocal());
  // The preset the buttons show as selected; null while a custom date range or
  // a zoom is in force, which is why it is not just `preferredHrs`.
  const [rangeHrs, setRangeHrs] = useState<number | null>(preferredHrs);

  // Data — both sides are returned by the same endpoints, so one fetch fills both.
  const [statusData, setStatusData] = useState<Record<Side, ExtruderStatusPoint[]>>(
    emptyBySide
  );
  const [motorData, setMotorData] = useState<Record<Side, Point[]>>(emptyBySide);
  const [speedData, setSpeedData] = useState<Record<Side, Point[]>>(emptyBySide);
  const [current, setCurrent] = useState<LamCurrentStatus | null>(null);
  const [currentLoading, setCurrentLoading] = useState(true);

  // Click-to-zoom marker state (shared across every chart)
  const [markers, setMarkers] = useState<number[]>([]);
  const [zoomed, setZoomed] = useState(false);

  // Loading flags
  const [loadingMotor, setLoadingMotor] = useState(false);
  const [loadingSpeed, setLoadingSpeed] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);

  // Live status panel (poll independent of the date range)
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    const load = async () => {
      try {
        const j = await api.getCurrentStatus(controller.signal);
        if (alive) setCurrent(j);
      } catch {
        /* keep last known */
      } finally {
        if (alive) setCurrentLoading(false);
      }
    };
    load();
    const t = setInterval(load, CURRENT_STATUS_REFRESH_MS);
    return () => {
      alive = false;
      controller.abort();
      clearInterval(t);
    };
  }, [api]);

  // Boolean position series → run/idle status for the events tables.
  // `silent` skips the loading overlay so the 60s auto-refresh doesn't flash.
  const fetchStatus = async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!start || !end) return;
    if (!silent) setLoadingEvents(true);
    try {
      const rows = await api.getStatus({
        start: nashvilleLocalToISO(start),
        end: nashvilleLocalToISO(end),
      });
      const parseSide = (key: string): ExtruderStatusPoint[] =>
        rows
          .map((r) => ({
            timestamp: toMs(r.timestamp),
            status: ((r as any)[key] ? 1 : 0) as 0 | 1,
          }))
          .filter((p) => Number.isFinite(p.timestamp));

      setStatusData({
        a: parseSide("extruder_a_position"),
        b: parseSide("extruder_b_position"),
      });
    } catch {
      setStatusData(emptyBySide());
    } finally {
      if (!silent) setLoadingEvents(false);
    }
  };

  // Motor current + screw speed for both sides.
  // `silent` skips the loading overlay so the 60s auto-refresh doesn't flash.
  const fetchMotorAndSpeed = async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!start || !end) return;
    // Adaptive server-side downsampling: toMs() already reads the bucketed
    // `period` rows; narrow windows come back at full resolution.
    const query = {
      start: nashvilleLocalToISO(start),
      end: nashvilleLocalToISO(end),
      max_points: CHART_MAX_POINTS,
    };

    if (!silent) setLoadingMotor(true);
    try {
      const rows = await api.getMotorCurrents(query);
      const parseCol = (col: string): Point[] =>
        rows
          .map((r) => ({
            timestamp: toMs(r.timestamp ?? r.period),
            value: (r as any)[col] == null ? null : Number((r as any)[col]),
          }))
          .filter(finite);
      setMotorData({
        a: parseCol("motor_current_a"),
        b: parseCol("motor_current_b"),
      });
    } catch {
      setMotorData(emptyBySide());
    } finally {
      if (!silent) setLoadingMotor(false);
    }

    if (!silent) setLoadingSpeed(true);
    try {
      const rows = await api.getScrewSpeeds(query);
      const parseCol = (col: string): Point[] =>
        rows
          .map((r) => ({
            timestamp: toMs(r.timestamp ?? r.period),
            value: (r as any)[col] == null ? null : Number((r as any)[col]),
          }))
          .filter(finite);
      setSpeedData({
        a: parseCol("screw_speed_a"),
        b: parseCol("screw_speed_b"),
      });
    } catch {
      setSpeedData(emptyBySide());
    } finally {
      if (!silent) setLoadingSpeed(false);
    }
  };

  // refetch on range change; reset shared brush
  useEffect(() => {
    setMarkers([]);
    fetchStatus();
    fetchMotorAndSpeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end]);

  // Auto-refresh chart data every minute WITHOUT resetting the zoom.
  // The date range (start/end) is untouched here, so the zoom window and any
  // markers are preserved across refreshes.
  useEffect(() => {
    const id = setInterval(() => {
      fetchStatus({ silent: true });
      fetchMotorAndSpeed({ silent: true });
    }, CHART_REFRESH_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end]);

  const anyLoading = loadingMotor || loadingSpeed || loadingEvents;

  // Charts render/fetch this window; data timestamps share the same
  // (Nashville-naive ms) space as start/end getTime().
  const chartDomain: [number, number] | null =
    start && end ? [start.getTime(), end.getTime()] : null;

  const fmtMarker = (ms: number) => format(new Date(ms), "MMM d, HH:mm");

  const applyQuickRange = (hrs: number) => {
    setPreferredHrs(hrs); // remembered for next time
    setRangeHrs(hrs);
    setStart(asNashvilleLocal(new Date(Date.now() - hrs * 3600_000)));
    setEnd(nowAsNashvilleLocal());
    setMarkers([]);
    setZoomed(false);
  };

  const handleChartClick = (t: number) =>
    setMarkers((prev) => (prev.length >= 2 ? [t] : [...prev, t]));

  // Zoom = set the date range to the marked band (refetches at finer resolution).
  const zoomToSelection = () => {
    if (markers.length !== 2) return;
    const [a, b] = [...markers].sort((x, y) => x - y);
    if (b <= a) return;
    setStart(new Date(a));
    setEnd(new Date(b));
    setRangeHrs(null);
    setMarkers([]);
    setZoomed(true);
  };

  const clearMarkers = () => setMarkers([]);

  const resetZoom = () => {
    setMarkers([]);
    setZoomed(false);
    setRangeHrs(preferredHrs);
    setStart(asNashvilleLocal(new Date(Date.now() - preferredHrs * 3600_000)));
    setEnd(nowAsNashvilleLocal());
  };

  // Side B may have no PLC tag for some signals yet — surface that.
  const sideBHasData = useMemo(
    () => speedData.b.length > 0 || motorData.b.length > 0,
    [speedData.b.length, motorData.b.length]
  );

  const statusChips = current && (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Chip
        size="small"
        label={`Winder: ${current.winder_running ? "Running" : "Stopped"}`}
        color={current.winder_running ? "success" : "default"}
      />
      <Chip
        size="small"
        label={`Densifier: ${current.densifier_status ? "Running" : "Stopped"}`}
        color={current.densifier_status ? "success" : "default"}
      />
      <Chip
        size="small"
        label={`Extruder A: ${current.extruder_a_position ? "In" : "Out"}`}
      />
      <Chip
        size="small"
        label={`Extruder B: ${current.extruder_b_position ? "In" : "Out"}`}
      />
      <Chip
        size="small"
        color={current.heater_fault_a_tripped.length ? "error" : "default"}
        label={
          current.heater_fault_a_tripped.length
            ? `Heater faults A: ${current.heater_fault_a_tripped.join(", ")}`
            : "Heater faults A: none"
        }
      />
      <Chip
        size="small"
        color={current.densifier_fault_tripped.length ? "error" : "default"}
        label={
          current.densifier_fault_tripped.length
            ? `Densifier faults: ${current.densifier_fault_tripped.join(", ")}`
            : "Densifier faults: none"
        }
      />
    </Stack>
  );

  return (
    <Grid
      container
      spacing={2}
      padding={{ xs: 1, sm: 2 }}
      maxWidth="xl"
      margin="auto"
    >
      {/* Live status panel */}
      <Grid item xs={12}>
        <Card>
          <CardContent sx={{ p: { xs: 1.25, sm: 2 } }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Live Status
              {current?.timestamp ? ` — ${current.timestamp.replace("T", " ")}` : ""}
            </Typography>
            <LoadingOverlay show={currentLoading} minHeight={48}>
              {statusChips ?? (
                <Typography variant="body2" color="text.secondary">
                  No status available.
                </Typography>
              )}
            </LoadingOverlay>
          </CardContent>
        </Card>
      </Grid>

      {/* Controls */}
      <Grid
        container
        spacing={2}
        padding={{ xs: 1, sm: 2 }}
        maxWidth="xl"
        margin="auto"
        alignItems="center"
        wrap="wrap"
      >
        <Grid item xs={12} sm={6} md={4}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="Start Date"
              value={start}
              onChange={(newValue) => {
                setStart(newValue);
                setRangeHrs(null);
              }}
              slots={{ textField: TextField }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: isMobile ? "small" : "medium",
                },
              }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="End Date"
              value={end}
              onChange={(newValue) => {
                setEnd(newValue);
                setRangeHrs(null);
              }}
              slots={{ textField: TextField }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: isMobile ? "small" : "medium",
                },
              }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Button
            variant="contained"
            fullWidth
            sx={{ height: { xs: 40, sm: "100%" } }}
            disabled={anyLoading}
            onClick={() => {
              fetchStatus();
              fetchMotorAndSpeed();
            }}
            size={isMobile ? "small" : "medium"}
          >
            {anyLoading ? "Loading…" : "Refresh"}
          </Button>
        </Grid>

        <Grid item xs={12}>
          <ToggleButtonGroup
            value={rangeHrs != null ? String(rangeHrs) : ""}
            exclusive
            onChange={(_, v) => v && applyQuickRange(Number(v))}
            fullWidth
            size={isMobile ? "small" : "medium"}
          >
            {QUICK_RANGES.map((h) => (
              <ToggleButton key={h} value={String(h)}>
                {h < 24 ? `${h}H` : `${h / 24}D`}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            If a date range is set, it overrides the quick range.
          </Typography>
        </Grid>
      </Grid>

      {!sideBHasData && (
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary">
            No data for Extruder B in this range — the PLC may not expose this
            tag yet.
          </Typography>
        </Grid>
      )}

      {/* Zoom controls (shared by every chart) */}
      <Grid item xs={12}>
        <Card>
          <CardContent sx={{ py: 1.5 }}>
            <ChartZoomToolbar
              markers={markers}
              zoomed={zoomed}
              onClearMarkers={clearMarkers}
              onResetZoom={resetZoom}
              formatTime={fmtMarker}
            />
          </CardContent>
        </Card>
      </Grid>

      {/* One card per extruder: motor current, screw speed, run events */}
      {SIDE_OPTIONS.map((opt) => (
        <Grid item xs={12} key={`extruder-${opt.id}`}>
          <Card>
            <CardContent sx={{ p: { xs: 1.25, sm: 2 } }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {opt.label}
              </Typography>

              <LoadingOverlay show={loadingMotor} minHeight={isMobile ? 220 : 340}>
                <MotorCurrentChart
                  data={motorData[opt.id]}
                  title="Motor Current (A)"
                  domain={chartDomain}
                  markers={markers}
                  onChartClick={handleChartClick}
                  onZoom={zoomToSelection}
                />
              </LoadingOverlay>

              <Divider sx={{ my: 2 }} />

              <LoadingOverlay show={loadingSpeed} minHeight={isMobile ? 220 : 340}>
                <ScrewSpeedChart
                  data={speedData[opt.id]}
                  title="Screw Speed (Hz)"
                  domain={chartDomain}
                  markers={markers}
                  onChartClick={handleChartClick}
                  onZoom={zoomToSelection}
                />
              </LoadingOverlay>

              <LoadingOverlay show={loadingEvents} minHeight={72}>
                <ExtruderEventTable2
                  extruderId={opt.id.toUpperCase()}
                  title="Run Events"
                  runData={statusData[opt.id]}
                  speedData={
                    speedData[opt.id].filter(finite) as {
                      timestamp: number;
                      value: number;
                    }[]
                  }
                  thresholds={thresholds}
                />
              </LoadingOverlay>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default LamExtruderDashboard;
